import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { CheckCircle2, X, ArrowRight, Zap, Sparkles, Crown, Building2, Shield } from "lucide-react";

const FadeUp = ({ children, delay = 0, className = "" }: {
  children: React.ReactNode; delay?: number; className?: string;
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const PLANS = [
  {
    name: "Essential", icon: Zap, price: { monthly: 0, annual: 0 },
    desc: "Your personalised vitamin plan — free forever.",
    color: "text-ay-muted", border: "border-ay-border", highlight: false, cta: "Start free",
    features: [
      { label: "Personalised vitamin plan built around you", included: true },
      { label: "NHS/SACN-aligned recommendations",     included: true },
      { label: "UK demographic & seasonal rules",      included: true },
      { label: "Drug-nutrient interaction checks",     included: true },
      { label: "5 profile updates / month",            included: true },
      { label: "Lab result interpretation",            included: false },
      { label: "Microbiome health reports",            included: false },
      { label: "Clinical literature Q&A",              included: false },
      { label: "Progress tracking",                    included: false },
      { label: "Priority support",                     included: false },
    ],
  },
  {
    name: "Pro", icon: Sparkles, price: { monthly: 9.99, annual: 7.99 },
    desc: "Deep health intelligence for the health-conscious.",
    color: "text-ay-cyan", border: "border-ay-cyan/40", highlight: true,
    cta: "Start 14-day trial", badge: "Most popular",
    features: [
      { label: "Everything in Essential",              included: true },
      { label: "Unlimited profile updates",            included: true },
      { label: "Lab result interpretation",            included: true },
      { label: "Microbiome health reports",            included: true },
      { label: "Seasonal plan updates",                included: true },
      { label: "Progress tracking & trends",           included: true },
      { label: "Clinical literature Q&A",              included: false },
      { label: "Regulatory-grade reports",             included: false },
      { label: "API access",                           included: false },
      { label: "Priority support",                     included: false },
    ],
  },
  {
    name: "Premium", icon: Crown, price: { monthly: 24.99, annual: 19.99 },
    desc: "Your complete AI health companion.",
    color: "text-violet-400", border: "border-violet-500/30", highlight: false,
    cta: "Start 14-day trial",
    features: [
      { label: "Everything in Pro",                    included: true },
      { label: "Clinical literature Q&A",              included: true },
      { label: "Regulatory-grade health reports",      included: true },
      { label: "AI health coach (coming soon)",        included: true },
      { label: "Export PDF/DOCX reports",              included: true },
      { label: "API access",                           included: true },
      { label: "Priority support",                     included: true },
      { label: "Early access to new features",         included: true },
      { label: "Dedicated account manager",            included: false },
      { label: "Custom integrations",                  included: false },
    ],
  },
];

const FAQS = [
  { q: "Is the free plan really free?",
    a: "Yes. Essential is free forever — no credit card required. You get a fully personalised vitamin plan based on your profile, updated up to 5 times per month." },
  { q: "What makes AlphaYou different from other supplement apps?",
    a: "AlphaYou is built on UK clinical data — NHS RDAs, SACN dietary reference values, NICE guidelines, and BNF drug interactions. Not US standards. Not generic advice. Your plan accounts for your UK region, ethnicity, season, diet, and medications." },
  { q: "Where is my data stored?",
    a: "All data is stored in eu-west-2 (London, UK). We never transfer data outside the UK. GDPR compliant by design. No PII in AI processing." },
  { q: "Can I cancel anytime?",
    a: "Yes. Cancel anytime from your account settings. No lock-in, no cancellation fees. You keep access until the end of your billing period." },
  { q: "Do you offer a B2B or enterprise plan?",
    a: "Yes. For NHS trusts, life sciences companies, CROs, and supplement brands wanting to embed AlphaYou's intelligence into their products. Contact us for custom pricing." },
  { q: "Is this medical advice?",
    a: "No. AlphaYou provides evidence-based nutritional guidance grounded in UK clinical guidelines. It is not a substitute for professional medical advice. Always consult your GP or pharmacist before starting any supplement regimen." },
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(true);

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative section-pad pt-32 pb-16">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="orb orb-violet w-[400px] h-[400px] -top-20 left-1/2 -translate-x-1/2 opacity-15" />
        <div className="container-ay relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="badge-cyan mb-6 mx-auto w-fit">Simple pricing</div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              Start free.<br />
              <span className="text-gradient-cyan">Upgrade when you're ready.</span>
            </h1>
            <p className="text-slate-400 text-xl max-w-xl mx-auto mb-8">
              Your personalised vitamin plan is free forever. Upgrade for deeper health intelligence.
            </p>
            <div className="inline-flex items-center gap-3 bg-ay-surface border border-ay-border rounded-xl p-1">
              <button onClick={() => setAnnual(false)}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${!annual ? "bg-ay-cyan text-ay-bg" : "text-ay-muted hover:text-white"}`}>
                Monthly
              </button>
              <button onClick={() => setAnnual(true)}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${annual ? "bg-ay-cyan text-ay-bg" : "text-ay-muted hover:text-white"}`}>
                Annual
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${annual ? "bg-ay-bg text-ay-cyan" : "bg-ay-emerald/20 text-ay-emerald"}`}>
                  Save 20%
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Plans */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="container-ay">
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {PLANS.map(({ name, icon: Icon, price, desc, color, border, highlight, cta, badge, features }, i) => (
              <FadeUp key={name} delay={i * 0.1}>
                <div className={`ay-card border ${border} relative ${highlight ? "shadow-cyan-glow" : ""}`}>
                  {badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="badge-cyan text-xs px-3 py-1">{badge}</span>
                    </div>
                  )}
                  <div className={`w-10 h-10 rounded-xl bg-ay-surface border ${border} flex items-center justify-center mb-4`}>
                    <Icon size={18} className={color} />
                  </div>
                  <h3 className={`font-black text-xl mb-1 ${color}`}>{name}</h3>
                  <p className="text-ay-muted text-sm mb-5">{desc}</p>
                  <div className="mb-6">
                    {price.monthly === 0 ? (
                      <div className="text-3xl font-black text-white">Free</div>
                    ) : (
                      <div className="flex items-end gap-1">
                        <span className="text-4xl font-black text-white">
                          £{annual ? price.annual : price.monthly}
                        </span>
                        <span className="text-ay-muted text-sm mb-1">/month</span>
                      </div>
                    )}
                    {price.monthly > 0 && annual && (
                      <p className="text-xs text-ay-emerald mt-1">
                        Billed annually · Save £{((price.monthly - price.annual) * 12).toFixed(2)}/year
                      </p>
                    )}
                  </div>
                  <Link to="/app"
                    className={highlight ? "btn-primary w-full justify-center mb-6" : "btn-outline w-full justify-center mb-6"}>
                    {cta} <ArrowRight size={14} />
                  </Link>
                  <ul className="space-y-2.5">
                    {features.map(({ label, included }) => (
                      <li key={label} className="flex items-center gap-2.5 text-sm">
                        {included
                          ? <CheckCircle2 size={14} className="text-ay-emerald flex-shrink-0" />
                          : <X size={14} className="text-ay-muted flex-shrink-0" />}
                        <span className={included ? "text-slate-300" : "text-ay-muted"}>{label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* B2B callout */}
      <section className="section-pad pt-0">
        <div className="container-ay">
          <FadeUp>
            <div className="ay-card border border-violet-500/30 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center flex-shrink-0">
                  <Building2 size={22} className="text-violet-400" />
                </div>
                <div>
                  <h3 className="font-black text-white text-xl mb-1">Building a health product?</h3>
                  <p className="text-slate-400 text-sm max-w-lg">
                    Embed AlphaYou's personalisation engine into your supplement brand, wellness app, or NHS service.
                    Custom pricing for B2B, white-label, and API integrations.
                  </p>
                </div>
              </div>
              <Link to="/contact" className="btn-outline flex-shrink-0">
                Talk to our team <ArrowRight size={14} />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* NHS callout */}
      <section className="section-pad pt-0">
        <div className="container-ay">
          <FadeUp>
            <div className="ay-card border border-ay-cyan/20 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-ay-cyan/10 border border-ay-cyan/20 flex items-center justify-center flex-shrink-0">
                  <Shield size={22} className="text-ay-cyan" />
                </div>
                <div>
                  <h3 className="font-black text-white text-xl mb-1">NHS & regulated organisations</h3>
                  <p className="text-slate-400 text-sm max-w-lg">
                    NHS DSP Toolkit alignment, SAML/SSO, VPC deployment, and dedicated compliance documentation.
                    Enterprise plan covers all regulated healthcare requirements.
                  </p>
                </div>
              </div>
              <Link to="/contact" className="btn-outline flex-shrink-0">
                Enterprise enquiry <ArrowRight size={14} />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad" style={{ background: "rgba(13,18,32,0.5)" }}>
        <div className="container-ay max-w-3xl">
          <FadeUp className="text-center mb-12">
            <h2 className="text-3xl font-black text-white mb-3">Frequently asked questions</h2>
            <p className="text-slate-400">Can't find your answer? <Link to="/contact" className="text-ay-cyan hover:underline">Contact us</Link>.</p>
          </FadeUp>
          <div className="space-y-4">
            {FAQS.map(({ q, a }, i) => (
              <FadeUp key={q} delay={i * 0.05}>
                <div className="ay-card">
                  <h4 className="font-semibold text-white mb-2">{q}</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">{a}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
